/**
 * APX WorkCenter — Client Documents Panel
 *
 * Displays Google Drive files for a specific client.
 * Automatically finds the client's folder in:
 *   APX ACCOUNTING INFO / ACCOUNTING FILES / [First Letter] / [Client Name] / [Year]
 *
 * Write capabilities (all users):
 *   - Upload files into any folder (stamped with initials + timestamp)
 *   - Create new subfolders (named, inside any folder)
 *
 * Delete capability (admin / controller only — Gus, Amy, Mr. B):
 *   - Permanently delete any file or folder from Drive
 */

import { useState, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Folder,
  FolderPlus,
  FileText,
  FileImage,
  File,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  FolderOpen,
  HardDrive,
  Upload,
  Trash2,
  X,
  Check,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string | null;
  size: string | null;
  webViewLink: string | null;
  iconLink: string | null;
  isFolder: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DELETE_ALLOWED_ROLES = ["super_admin", "admin", "controller"];

function canDelete(role: string | undefined): boolean {
  return DELETE_ALLOWED_ROLES.includes(role ?? "");
}

function getFileIcon(mimeType: string, isFolder: boolean) {
  if (isFolder) return <Folder className="w-4 h-4 text-[oklch(0.55_0.10_75)] flex-shrink-0" />;
  if (mimeType.startsWith("image/")) return <FileImage className="w-4 h-4 text-[oklch(0.35_0.01_240)] flex-shrink-0" />;
  if (mimeType === "application/pdf") return <FileText className="w-4 h-4 text-[oklch(0.35_0.01_240)] flex-shrink-0" />;
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel"))
    return <FileText className="w-4 h-4 text-[oklch(0.35_0.01_240)] flex-shrink-0" />;
  if (mimeType.includes("document") || mimeType.includes("word"))
    return <FileText className="w-4 h-4 text-[oklch(0.35_0.01_240)] flex-shrink-0" />;
  return <File className="w-4 h-4 text-[oklch(0.35_0.01_240)] flex-shrink-0" />;
}

function formatFileSize(size: string | null): string {
  if (!size) return "";
  const bytes = parseInt(size, 10);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]); // strip data URL prefix
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

interface ClientDocumentsProps {
  clientName: string;
}

export default function ClientDocuments({ clientName }: ClientDocumentsProps) {
  const { user } = useAuth();
  const [selectedYearId, setSelectedYearId] = useState<string | undefined>(undefined);

  // Upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // New folder state
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);

  // Delete confirm state
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const utils = trpc.useUtils();

  const { data, isLoading, refetch } = trpc.drive.listClientFiles.useQuery(
    { clientName, yearFolderId: selectedYearId },
    { enabled: !!clientName }
  );

  const { data: authData } = trpc.drive.isAuthorized.useQuery();
  const { data: authUrlData } = trpc.drive.getAuthUrl.useQuery(undefined, {
    enabled: authData?.authorized === false,
  });

  const uploadMutation = trpc.drive.uploadFile.useMutation({
    onSuccess: () => {
      toast.success("File uploaded successfully");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const createFolderMutation = trpc.drive.createFolder.useMutation({
    onSuccess: (result) => {
      toast.success(`Folder "${result.name}" created`);
      setShowNewFolder(false);
      setNewFolderName("");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteFileMutation = trpc.drive.deleteFile.useMutation({
    onSuccess: () => {
      toast.success("Deleted successfully");
      setDeletingId(null);
      refetch();
    },
    onError: (err) => {
      toast.error(err.message);
      setDeletingId(null);
    },
  });

  // Determine the active folder ID for uploads/new folders
  const activeFolderId = selectedYearId ?? data?.folderMatch?.folderId ?? null;

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length || !activeFolderId) return;

    for (const file of files) {
      if (file.size > 16 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 16 MB limit`);
        continue;
      }
      setUploading(true);
      try {
        const base64 = await toBase64(file);
        await uploadMutation.mutateAsync({
          folderId: activeFolderId,
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          fileBase64: base64,
          uploaderEmail: user?.email ?? undefined,
          uploaderName: user?.name ?? undefined,
        });
      } finally {
        setUploading(false);
      }
    }
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleCreateFolder() {
    if (!newFolderName.trim() || !activeFolderId) return;
    setCreatingFolder(true);
    try {
      await createFolderMutation.mutateAsync({
        parentFolderId: activeFolderId,
        folderName: newFolderName.trim(),
        uploaderEmail: user?.email ?? undefined,
      });
    } finally {
      setCreatingFolder(false);
    }
  }

  async function handleDelete(fileId: string) {
    if (!canDelete(user?.role)) return;
    setDeletingId(fileId);
    await deleteFileMutation.mutateAsync({
      fileId,
      callerRole: user?.role ?? "",
      callerEmail: user?.email ?? undefined,
    });
  }

  // ── Drive not authorized ──────────────────────────────────────────────────
  if (!authData?.authorized) {
    return (
      <div className="rounded-lg border border-[oklch(0.82_0.02_85)] bg-[oklch(0.96_0.01_85)] p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-[oklch(0.35_0.01_240)] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[oklch(0.08_0.01_240)]">Drive not connected</p>
            <p className="text-sm text-[oklch(0.28_0.03_240)] mt-1">
              Authorize apxadvisor@gmail.com to view client documents from Google Drive.
            </p>
            {authUrlData?.url && (
              <a href={authUrlData.url} target="_blank" rel="noopener noreferrer" className="inline-block mt-2">
                <Button size="sm" variant="outline" className="h-7 text-sm border-[oklch(0.78_0.02_240)] text-[oklch(0.08_0.01_240)] hover:bg-[oklch(0.90_0.01_85)]">
                  Connect Google Drive
                </Button>
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 justify-center text-[oklch(0.35_0.02_240)] text-sm">
        <RefreshCw className="w-4 h-4 animate-spin" />
        Loading documents…
      </div>
    );
  }

  const folderMatch = data?.folderMatch;
  const yearFolders = data?.yearFolders ?? [];
  const files = data?.files ?? [];

  // ── No folder found ───────────────────────────────────────────────────────
  if (!folderMatch?.folderId) {
    return (
      <div className="rounded-lg border border-[oklch(0.82_0.02_85)] bg-[oklch(0.96_0.01_85)] p-4">
        <div className="flex items-start gap-3">
          <FolderOpen className="w-4 h-4 text-[oklch(0.35_0.02_240)] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[oklch(0.08_0.01_240)]">No Drive folder found</p>
            <p className="text-sm text-[oklch(0.28_0.03_240)] mt-1">
              No folder matching "{clientName}" was found in ACCOUNTING FILES.
            </p>
            <a href="https://drive.google.com" target="_blank" rel="noopener noreferrer" className="inline-block mt-2">
              <Button size="sm" variant="outline" className="h-7 text-sm text-[oklch(0.08_0.01_240)] border-[oklch(0.78_0.02_240)] hover:bg-[oklch(0.90_0.01_85)]">
                <HardDrive className="w-3.5 h-3.5 mr-1" />
                Open Drive
              </Button>
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Main panel ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Folder className="w-4 h-4 text-[oklch(0.55_0.10_75)] flex-shrink-0" />
          <span className="text-sm font-medium text-[oklch(0.18_0.04_240)] truncate">
            {folderMatch.folderName}
          </span>
          {folderMatch.matchType === "fuzzy" && (
            <Badge variant="outline" className="text-xs border-[oklch(0.78_0.01_240)]/50 text-[oklch(0.35_0.01_240)] h-5 flex-shrink-0">
              fuzzy
            </Badge>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
          {/* New Folder */}
          {activeFolderId && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowNewFolder((v) => !v)}
              className="h-7 text-xs border-[oklch(0.78_0.01_240)]/60 text-[oklch(0.18_0.04_240)] hover:bg-[oklch(0.90_0.01_85)]"
            >
              <FolderPlus className="w-3.5 h-3.5 mr-1" />
              New Folder
            </Button>
          )}

          {/* Upload File */}
          {activeFolderId && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                className="h-7 text-xs border-[oklch(0.78_0.01_240)]/60 text-[oklch(0.18_0.04_240)] hover:bg-[oklch(0.90_0.01_85)]"
              >
                {uploading ? (
                  <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <Upload className="w-3.5 h-3.5 mr-1" />
                )}
                {uploading ? "Uploading…" : "Upload"}
              </Button>
            </>
          )}

          {/* View in Drive */}
          {folderMatch.folderId && (
            <a
              href={`https://drive.google.com/drive/folders/${folderMatch.folderId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-[oklch(0.78_0.01_240)]/60 text-[oklch(0.18_0.04_240)] hover:bg-[oklch(0.90_0.01_85)]"
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1" />
                Drive
              </Button>
            </a>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            className="h-7 w-7 p-0 text-[oklch(0.35_0.02_240)] hover:text-[oklch(0.35_0.01_240)]"
            title="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* New folder input */}
      {showNewFolder && activeFolderId && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-[oklch(0.96_0.01_85)] border border-[oklch(0.82_0.02_85)]">
          <FolderPlus className="w-4 h-4 text-[oklch(0.55_0.10_75)] flex-shrink-0" />
          <Input
            autoFocus
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateFolder();
              if (e.key === "Escape") { setShowNewFolder(false); setNewFolderName(""); }
            }}
            placeholder="Folder name…"
            className="h-7 text-sm flex-1 bg-white border-[oklch(0.82_0.02_85)] text-[oklch(0.08_0.01_240)]"
          />
          <Button
            size="sm"
            disabled={!newFolderName.trim() || creatingFolder}
            onClick={handleCreateFolder}
            className="h-7 w-7 p-0 bg-[oklch(0.72_0.14_75)] hover:bg-[oklch(0.65_0.14_75)] text-white"
          >
            {creatingFolder ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setShowNewFolder(false); setNewFolderName(""); }}
            className="h-7 w-7 p-0 text-[oklch(0.35_0.02_240)]"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      {/* Year folder tabs */}
      {yearFolders.length > 0 && (
        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => setSelectedYearId(undefined)}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              !selectedYearId
                ? "bg-[oklch(0.92_0.005_240)]/20 text-[oklch(0.35_0.01_240)] border border-[oklch(0.78_0.01_240)]/40"
                : "text-[oklch(0.08_0.01_240)] hover:text-[oklch(0.28_0.04_240)] border border-transparent hover:border-[oklch(0.28_0.04_240)]"
            }`}
          >
            All Years
          </button>
          {yearFolders.map((yf) => (
            <button
              key={yf.id}
              onClick={() => setSelectedYearId(yf.id)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                selectedYearId === yf.id
                  ? "bg-[oklch(0.92_0.005_240)]/20 text-[oklch(0.35_0.01_240)] border border-[oklch(0.78_0.01_240)]/40"
                  : "text-[oklch(0.08_0.01_240)] hover:text-[oklch(0.28_0.04_240)] border border-transparent hover:border-[oklch(0.28_0.04_240)]"
              }`}
            >
              {yf.name}
            </button>
          ))}
        </div>
      )}

      {/* File list */}
      {files.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-[oklch(0.35_0.02_240)]">
          <FolderOpen className="w-8 h-8 mb-2 opacity-40" />
          <p className="text-sm">No files found</p>
          {selectedYearId && (
            <button
              onClick={() => setSelectedYearId(undefined)}
              className="text-sm text-[oklch(0.35_0.01_240)] hover:underline mt-1"
            >
              Show all years
            </button>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-[oklch(0.82_0.02_85)] overflow-hidden">
          {files.map((file, i) => (
            <div
              key={file.id}
              className={`flex items-center gap-3 px-3 py-2.5 hover:bg-[oklch(0.93_0.01_85)] transition-colors group ${
                i > 0 ? "border-t border-[oklch(0.88_0.01_85)]" : ""
              }`}
            >
              {getFileIcon(file.mimeType, file.isFolder)}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[oklch(0.08_0.01_240)] truncate">{file.name}</p>
                <p className="text-xs text-[oklch(0.35_0.02_240)]">
                  {formatDate(file.modifiedTime)}
                  {file.size && ` · ${formatFileSize(file.size)}`}
                </p>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                {/* Open in Drive */}
                {file.webViewLink && (
                  <a href={file.webViewLink} target="_blank" rel="noopener noreferrer">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-[oklch(0.08_0.01_240)] hover:bg-[oklch(0.90_0.01_85)]"
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1" />
                      Open
                    </Button>
                  </a>
                )}

                {/* Delete — admin/controller only */}
                {canDelete(user?.role) && (
                  deletingId === file.id ? (
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(file.id)}
                        className="h-7 px-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        <Check className="w-3.5 h-3.5 mr-1" />
                        Confirm
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeletingId(null)}
                        className="h-7 w-7 p-0 text-[oklch(0.35_0.02_240)]"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDeletingId(file.id)}
                      className="h-7 w-7 p-0 text-[oklch(0.35_0.02_240)] hover:text-red-600 hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <p className="text-xs text-[oklch(0.35_0.02_240)] text-right">
          {files.length} item{files.length !== 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
}
